import { useCallback, useEffect, useRef, useState } from 'react'
import { sfxSubmit, sfxTick } from '../gameslib/audio'
import { hapticHoldStart, hapticWatchEnd } from '../gameslib/haptic'
import { useGameSettings } from '../gameslib/gameSettings'
import { scoreFromError } from '../gameslib/score'
import {
  loadShapeImage,
  preloadShapeAssets,
  TIME_SHAPE_ID,
} from '../gameslib/shapeAssets'
import { Countdown } from '../gamesshared/Countdown'
import { GameShell, IntroScreen, PlayerBanner } from '../gamesshared/IntroScreen'
import { PassDevice, PlayerSetup, WinnerScreen } from '../gamesshared/Multiplayer'
import { RoundResult, TotalScreen } from '../gamesshared/RoundResult'
import { TutorialCoach } from '../gamesshared/TutorialCoach'
import { useTutorial } from '../gameslib/useTutorial'

function randomDurationMs() {
  return 1000 + Math.floor(Math.random() * 4001)
}

/** Visual grow duration ≠ real target; watch vs hold use different rates */
function randomVisualMs(targetMs) {
  const mult = 0.45 + Math.random() * 1.2
  return Math.max(600, targetMs * mult)
}

function artLayout(img, w, h, dpr) {
  const cx = w / 2
  const cy = h / 2
  // Logo sits slightly above the circle center; circle stays centered
  const logoLift = 11 * dpr
  // Circle first, then fit SVG fully inside with margin (avoids clipping)
  const ringR = Math.min(w, h) * 0.36
  const inner = ringR * 0.78
  const aspect = (img.width || img.naturalWidth || 1) / (img.height || img.naturalHeight || 1)
  let dw
  let dh
  if (aspect >= 1) {
    dw = inner * 2
    dh = dw / aspect
  } else {
    dh = inner * 2
    dw = dh * aspect
  }
  // Ensure long side still fits in the disc
  const diag = Math.hypot(dw, dh) / 2
  if (diag > inner) {
    const s = inner / diag
    dw *= s
    dh *= s
  }
  return {
    cx,
    cy,
    dw,
    dh,
    dx: cx - dw / 2,
    dy: cy - dh / 2 - logoLift,
    ringR,
  }
}

const CARD_BG = '#001E3A'

/** White filled circle + original SVG + pulsing ring */
function drawTimeArt(ctx, dimImg, markImg, w, h, progress, dpr, now) {
  const base = markImg || dimImg
  if (!base) return

  const { cx, cy, dw, dh, dx, dy, ringR } = artLayout(base, w, h, dpr)

  // Increase pulse travel so the ring "breathes" more (more distance)
  const beat = 0.3 + 0.5 * Math.sin((now / 1000) * Math.PI * 2 * 1)
  const pulse = 1.000 + beat * 0.035 // Increased multiplier for more distance
  const strokeAlpha = 0.5 + beat * 0.18
  const r = ringR * pulse

  // Filled white disc — tight soft edge, not a huge bloom
  ctx.save()
  // ctx.shadowColor = `rgba(255,255,255,${0.28 + beat * 0.2})`
  ctx.shadowBlur = (6 + beat * 4) * dpr
  ctx.fillStyle = '#ffffff'
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.fill()
  ctx.restore()

  // SVG as-is (already filled), clipped to the white disc
  if (markImg) {
    ctx.save()
    ctx.beginPath()
    ctx.arc(cx, cy, r, 0, Math.PI * 2)
    ctx.clip()
    ctx.drawImage(markImg, dx, dy, dw, dh)
    ctx.restore()
  }

  // Pulse stroke on the edge
  ctx.save()
  ctx.strokeStyle = `rgba(255,255,255,${strokeAlpha})`
  ctx.lineWidth = (7 + beat * 5) * dpr
  ctx.shadowColor = `rgba(255,255,255,${0.3 + beat * 0.25})`
  ctx.shadowBlur = (5 + beat * 4) * dpr
  ctx.beginPath()
  ctx.arc(cx, cy, r, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
}

export function TimeGame() {
  const { rounds, setRounds, setModeLabel } = useGameSettings()
  const tutorial = useTutorial('time')
  const [phase, setPhase] = useState('intro')
  const [difficulty, setDifficulty] = useState('easy')
  const [round, setRound] = useState(0)
  const [targets, setTargets] = useState([])
  const [heldMs, setHeldMs] = useState(0)
  const [holding, setHolding] = useState(false)
  const [readyLeftMs, setReadyLeftMs] = useState(0)
  const [scores, setScores] = useState([])
  const [lastScore, setLastScore] = useState(0)

  const [playMode, setPlayMode] = useState('solo')
  const [players, setPlayers] = useState([])
  const [playerIndex, setPlayerIndex] = useState(0)

  const holdStart = useRef(null)
  const holdingRef = useRef(false)
  const drawRaf = useRef(0)
  const holdRaf = useRef(0)
  const canvasRef = useRef(null)
  const holdAreaRef = useRef(null)
  const nextRef = useRef(null)
  const stimStart = useRef(0)
  const targetRef = useRef(0)
  const phaseRef = useRef(phase)
  const artRef = useRef({ dim: null, mark: null, shapeId: null })
  const stimVisualMs = useRef(2000)
  const inputVisualMs = useRef(2000)
  const difficultyRef = useRef(difficulty)
  const tutorialRef = useRef(tutorial)
  difficultyRef.current = difficulty
  tutorialRef.current = tutorial

  const target = targets[round]
  targetRef.current = target || 0
  phaseRef.current = phase
  const currentPlayer = players[playerIndex]
  const playRounds = tutorial.active ? 1 : rounds

  useEffect(() => {
    setModeLabel(playMode === 'multi' ? 'multi' : 'solo')
  }, [playMode, setModeLabel])

  useEffect(() => {
    void preloadShapeAssets()
  }, [])

  const loadRoundArt = useCallback(() => {
    const shapeId = TIME_SHAPE_ID
    artRef.current = { dim: null, mark: null, shapeId }
    void loadShapeImage(shapeId).then((mark) => {
      if (artRef.current.shapeId !== shapeId) return
      if (mark) artRef.current = { dim: null, mark, shapeId }
    })
  }, [])

  const clearHold = () => {
    setHeldMs(0)
    setHolding(false)
    holdStart.current = null
    holdingRef.current = false
    cancelAnimationFrame(holdRaf.current)
  }

  const makeTargets = (count = rounds) => Array.from({ length: count }, () => randomDurationMs())

  const resetTurn = (shared = targets) => {
    setScores([])
    setRound(0)
    setLastScore(0)
    clearHold()
    if (shared?.length) setTargets(shared)
  }

  const beginPlay = (asTutorial) => {
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
    if (asTutorial) tutorial.start('watch')
    else if (tutorial.active) tutorial.finish()
    const next = makeTargets(asTutorial ? 1 : rounds)
    setTargets(next)
    resetTurn(next)
    setPhase('countdown')
  }

  const startSolo = () => beginPlay(tutorial.needsTutorial)
  const startTeach = () => beginPlay(true)

  const exitTutorial = () => {
    clearHold()
    tutorial.skip()
    setPhase('intro')
    setPlayMode('solo')
    setPlayers([])
    setPlayerIndex(0)
  }

  const confirmPlayers = (list) => {
    if (tutorial.active) tutorial.finish()
    const next = makeTargets()
    setTargets(next)
    setPlayers(list.map((p) => ({ ...p, scores: [] })))
    setPlayerIndex(0)
    setPlayMode('multi')
    resetTurn(next)
    setPhase('pass')
  }

  const finishPlayerTurn = (finalScores) => {
    if (playMode === 'solo') {
      setPhase('total')
      return
    }
    const updated = players.map((p, i) => (i === playerIndex ? { ...p, scores: finalScores } : p))
    setPlayers(updated)
    if (playerIndex >= players.length - 1) setPhase('winner')
    else {
      setPlayerIndex((i) => i + 1)
      setPhase('pass')
    }
  }

  const onCountdownDone = useCallback(() => {
    const tgt = targetRef.current || 2000
    const diff = difficultyRef.current
    const tut = tutorialRef.current
    // Variable watch/hold speeds only on hard & brutal; easy keeps matched pace
    if (!tut.active && (diff === 'hard' || diff === 'brutal')) {
      stimVisualMs.current = randomVisualMs(tgt)
      let inputMs = randomVisualMs(tgt)
      let guard = 0
      const minGap = diff === 'brutal' ? tgt * 0.35 : tgt * 0.25
      while (Math.abs(inputMs - stimVisualMs.current) < minGap && guard < 8) {
        inputMs = randomVisualMs(tgt)
        guard += 1
      }
      inputVisualMs.current = inputMs
    } else {
      stimVisualMs.current = tgt
      inputVisualMs.current = tgt
    }
    loadRoundArt()
    stimStart.current = performance.now()
    if (tut.active) tut.go('watch')
    setPhase('stimulus')
  }, [loadRoundArt])

  const onStimulusDone = useCallback(() => {
    clearHold()
    hapticWatchEnd()
    if (tutorialRef.current.active) tutorialRef.current.go('hold')
    setReadyLeftMs(1500)
    setPhase('ready')
  }, [])

  useEffect(() => {
    if (phase !== 'stimulus' || !target) return
    const id = window.setTimeout(onStimulusDone, target)
    return () => window.clearTimeout(id)
  }, [phase, target, onStimulusDone])

  // Brief get-ready timer before hold starts
  useEffect(() => {
    if (phase !== 'ready') return undefined
    const start = performance.now()
    const total = 1500
    let raf = 0
    const tick = (now) => {
      const left = Math.max(0, total - (now - start))
      setReadyLeftMs(left)
      if (left <= 0) {
        setPhase('input')
        return
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [phase])
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || (phase !== 'input' && phase !== 'stimulus')) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const resize = () => {
      const parent = canvas.parentElement
      if (!parent) return
      canvas.width = parent.clientWidth * devicePixelRatio
      canvas.height = parent.clientHeight * devicePixelRatio
    }
    resize()

    const draw = (now) => {
      const w = canvas.width
      const h = canvas.height
      const dpr = devicePixelRatio || 1
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = CARD_BG
      ctx.fillRect(0, 0, w, h)

      let progress = 0
      if (phaseRef.current === 'stimulus') {
        const elapsed = performance.now() - stimStart.current
        progress = elapsed / Math.max(stimVisualMs.current, 1)
      } else if (holdingRef.current && holdStart.current != null) {
        const elapsed = performance.now() - holdStart.current
        progress = elapsed / Math.max(inputVisualMs.current, 1)
      }

      const { dim, mark } = artRef.current
      drawTimeArt(ctx, dim, mark, w, h, progress, dpr, now)

      drawRaf.current = requestAnimationFrame(draw)
    }

    drawRaf.current = requestAnimationFrame(draw)
    window.addEventListener('resize', resize)
    return () => {
      cancelAnimationFrame(drawRaf.current)
      window.removeEventListener('resize', resize)
    }
  }, [phase])

  const beginHold = (e) => {
    if (phase !== 'input' || holdingRef.current) return
    e.currentTarget.setPointerCapture?.(e.pointerId)
    holdingRef.current = true
    setHolding(true)
    hapticHoldStart()
    void sfxTick()
    holdStart.current = performance.now()
    setHeldMs(0)
    if (tutorial.active && tutorial.step === 'hold') tutorial.go('release')

    const tick = () => {
      if (!holdingRef.current || holdStart.current == null) return
      setHeldMs(performance.now() - holdStart.current)
      holdRaf.current = requestAnimationFrame(tick)
    }
    cancelAnimationFrame(holdRaf.current)
    holdRaf.current = requestAnimationFrame(tick)
  }

  const endHold = () => {
    if (!holdingRef.current || holdStart.current == null) return
    cancelAnimationFrame(holdRaf.current)
    const ms = performance.now() - holdStart.current
    holdingRef.current = false
    holdStart.current = null
    setHolding(false)
    setHeldMs(ms)
    void sfxSubmit()
    const err = Math.abs(ms - target)
    const s = scoreFromError(err, target * 0.85)
    setLastScore(s)
    setScores((prev) => [...prev, s])
    if (tutorial.active) tutorial.go('done')
    setPhase('result')
  }

  const next = () => {
    if (tutorial.active) {
      tutorial.finish()
      setPhase('intro')
      return
    }
    if (round + 1 >= playRounds) {
      const finalScores = scores.length >= playRounds ? scores : [...scores, lastScore]
      finishPlayerTurn(finalScores)
    } else {
      setRound((r) => r + 1)
      clearHold()
      setPhase('countdown')
    }
  }

  const coach =
    tutorial.active &&
    ({
      watch: { message: 'Watch how long it fills', gesture: 'watch', anchor: 'center' },
      hold: { message: 'Hold to grow yours', gesture: 'hold', anchor: 'center' },
      release: { message: 'Release when it matches', gesture: 'tap', anchor: 'center' },
      done: { message: 'Closer timing scores higher', gesture: 'tap', anchor: 'submit' },
    }[tutorial.step] || null)
  const coachTarget =
    tutorial.step === 'done'
      ? nextRef
      : tutorial.step === 'hold' || tutorial.step === 'release'
        ? holdAreaRef
        : null

  return (
    <GameShell>
      {phase === 'intro' && (
        <IntroScreen
          title="time"
          blurb={`You experience time constantly. Let's see how good you are at measuring it.\nWe'll show you ${rounds} duration${rounds === 1 ? '' : 's'}, then you'll try to recreate them from memory.`}
          onSolo={startSolo}
          onMulti={() => setPhase('setup')}
          onTeach={startTeach}
          difficulty={difficulty}
          onDifficulty={setDifficulty}
          rounds={rounds}
          onRounds={setRounds}
        />
      )}

      {phase === 'setup' && (
        <PlayerSetup onConfirm={confirmPlayers} onBack={() => setPhase('intro')} rounds={rounds} onRounds={setRounds} />
      )}

      {phase === 'pass' && currentPlayer && (
        <PassDevice
          player={currentPlayer}
          index={playerIndex}
          total={players.length}
          rounds={rounds}
          onReady={() => {
            resetTurn(targets)
            setPhase('countdown')
          }}
        />
      )}

      {phase === 'countdown' && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <Countdown onDone={onCountdownDone} />
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'stimulus' && (
        <div className="stage">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <canvas ref={canvasRef} className="time-canvas" />
          <p className="time-hold__hint">watch</p>
          <div className="watermark">Memory</div>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'ready' && (
        <div className="stage time-ready">
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          <div className="time-vtimer time-vtimer--ready" aria-live="polite">
            <span className="time-vtimer__num">{Math.max(1, Math.ceil(readyLeftMs / 500))}</span>
            <span className="time-vtimer__label">get ready — then hold</span>
          </div>
          <p className="time-hold__hint">your turn next</p>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} />}
        </div>
      )}

      {phase === 'input' && (
        <div
          ref={holdAreaRef}
          className={`time-hold${tutorial.step === 'hold' || tutorial.step === 'release' ? ' is-tutor-pulse' : ''}`}
          onPointerDown={beginHold}
          onPointerUp={endHold}
          onPointerCancel={endHold}
        >
          {playMode === 'multi' && <PlayerBanner name={currentPlayer?.name} />}
          {!tutorial.active && (
            <div className="round-pill">
              {round + 1} / {playRounds}
            </div>
          )}
          <canvas ref={canvasRef} className="time-canvas" />
          <p className={`time-hold__hint ${holding ? 'is-holding' : ''}`}>
            {holding ? (
              <>
                <span className="time-hold__live">{(heldMs / 1000).toFixed(2)}</span>
                <span className="time-hold__unit">s</span>
              </>
            ) : (
              'your turn — hold to match'
            )}
          </p>
          <div className="watermark">Memory</div>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'result' && (
        <div className="screen">
          <RoundResult
            score={lastScore}
            round={tutorial.active ? 1 : round + 1}
            totalRounds={playRounds}
            onNext={next}
            nextButtonRef={nextRef}
            scoreColor="#fff"
            layout="stack"
          >
            <div className="pitch-compare">
              <p>
                You held: <strong>{(heldMs / 1000).toFixed(2)}s</strong>
              </p>
              <p>
                Target: <strong>{(target / 1000).toFixed(2)}s</strong>
              </p>
            </div>
          </RoundResult>
          {coach && <TutorialCoach {...coach} onSkip={exitTutorial} targetRef={coachTarget} />}
        </div>
      )}

      {phase === 'total' && <TotalScreen scores={scores} onReplay={startSolo} />}

      {phase === 'winner' && (
        <WinnerScreen
          players={players}
          onReplay={() => {
            const nextTargets = makeTargets()
            setTargets(nextTargets)
            setPlayers((prev) => prev.map((p) => ({ ...p, scores: [] })))
            setPlayerIndex(0)
            resetTurn(nextTargets)
            setPhase('pass')
          }}
          onHome={() => {
            setPhase('intro')
            setPlayMode('solo')
            setPlayers([])
            setPlayerIndex(0)
          }}
        />
      )}
    </GameShell>
  )
}
